import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Stack,
  LinearProgress,
  alpha,
  useTheme,
} from '@mui/material';
import { format, startOfMonth, endOfMonth, subMonths, subDays } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import {
  People,
  Receipt,
  TrendingUp,
  TrendingDown,
  Refresh,
  Warning,
  CheckCircle,
  HourglassEmpty,
  RequestQuote,
  WhatsApp,
  Assessment,
  Router,
  ArrowForward,
  Today,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import billService from '../services/billService';
import customerService from '../services/customerService';
import transactionService from '../services/transactionService';
import routerService from '../services/routerService';
import spopService from '../services/spopService';
import whatsappService from '../services/whatsappService';
import { useAuth } from '../hooks/useAuth';
import { Customer } from '../types';

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  trend?: number;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  loading,
  trend,
  onClick,
}) => {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        borderLeft: `4px solid ${color}`,
        bgcolor: alpha(color, 0.04),
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick
          ? { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] }
          : undefined,
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={28} sx={{ my: 1 }} />
            ) : (
              <Typography variant="h5" fontWeight={700} sx={{ color }} noWrap>
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && !loading && (
              <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                {trend >= 0 ? (
                  <TrendingUp fontSize="small" color="success" />
                ) : (
                  <TrendingDown fontSize="small" color="error" />
                )}
                <Typography variant="caption" color={trend >= 0 ? 'success.main' : 'error.main'}>
                  {Math.abs(trend)}% vs bulan lalu
                </Typography>
              </Stack>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48, ml: 1 }}>{icon}</Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
};

const QUICK_LINKS = [
  { label: 'Kelola Tagihan', path: '/bills', icon: <RequestQuote />, color: '#ed6c02' },
  { label: 'Transaksi', path: '/transactions', icon: <Receipt />, color: '#1976d2' },
  { label: 'Pelanggan', path: '/customers', icon: <People />, color: '#2e7d32' },
  { label: 'Log Antrian WA', path: '/whatsapp/queue/log', icon: <WhatsApp />, color: '#25D366' },
  { label: 'Pendapatan SPOP', path: '/reports/spop-revenue', icon: <Assessment />, color: '#9c27b0' },
  { label: 'Router', path: '/routers', icon: <Router />, color: '#0288d1' },
];

const BILL_STATUS_COLORS: Record<string, string> = {
  Lunas: '#2e7d32',
  Pending: '#ed6c02',
  Tunggakan: '#d32f2f',
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const spopFilter = user?.role === 'operator' && user?.spop_id ? user.spop_id : undefined;

  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const prevMonthDate = subMonths(now, 1);

  const monthLabel = format(now, 'MMMM yyyy', { locale: localeId });
  const monthRange = useMemo(
    () => ({
      start_date: format(startOfMonth(now), 'yyyy-MM-dd'),
      end_date: format(endOfMonth(now), 'yyyy-MM-dd'),
    }),
    [month, year]
  );

  const customerFilters = spopFilter ? { spop_id: spopFilter } : undefined;

  const { data: billStats, isLoading: billLoading } = useQuery({
    queryKey: ['dashboardBillStats', month, year, spopFilter],
    queryFn: async () => {
      const res = await billService.getBillingStats(String(month), String(year));
      return res.data;
    },
  });

  const { data: prevBillStats } = useQuery({
    queryKey: ['dashboardBillStats', prevMonthDate.getMonth() + 1, prevMonthDate.getFullYear(), spopFilter],
    queryFn: async () => {
      const res = await billService.getBillingStats(
        String(prevMonthDate.getMonth() + 1),
        String(prevMonthDate.getFullYear())
      );
      return res.data;
    },
  });

  const { data: customerTotal, isLoading: customersLoading } = useQuery({
    queryKey: ['dashboardCustomerTotal', spopFilter],
    queryFn: () => customerService.getCustomers(1, 1, undefined, customerFilters),
  });

  const { data: customerActive } = useQuery({
    queryKey: ['dashboardCustomerActive', spopFilter],
    queryFn: () => customerService.getCustomers(1, 1, undefined, { ...customerFilters, status: 'active' }),
  });

  const { data: customerSuspended } = useQuery({
    queryKey: ['dashboardCustomerSuspended', spopFilter],
    queryFn: () =>
      customerService.getCustomers(1, 1, undefined, { ...customerFilters, status: 'suspended' }),
  });

  const { data: recentBills, isLoading: billsLoading } = useQuery({
    queryKey: ['dashboardRecentBills', month, year, spopFilter],
    queryFn: () =>
      billService.getBills({
        page: 1,
        limit: 8,
        month: String(month).padStart(2, '0'),
        year: String(year),
        ...(spopFilter ? { spop_id: spopFilter } : {}),
      }),
  });

  const { data: recentCustomers, isLoading: recentCustomersLoading } = useQuery({
    queryKey: ['dashboardRecentCustomers', spopFilter],
    queryFn: () => customerService.getCustomers(1, 5, undefined, customerFilters),
  });

  const { data: txStats, isLoading: txLoading } = useQuery({
    queryKey: ['dashboardTxStats', monthRange, spopFilter],
    queryFn: () =>
      transactionService.getTransactionStats({
        ...monthRange,
        ...(spopFilter ? { spop_id: spopFilter } : {}),
      }),
  });

  const { data: revenueChart, isLoading: chartLoading } = useQuery({
    queryKey: ['dashboardRevenueChart', spopFilter],
    queryFn: () =>
      transactionService.getRevenueChart('daily', spopFilter ? { spop_id: spopFilter } : undefined),
  });

  const { data: waQueueStats } = useQuery({
    queryKey: ['dashboardWaQueue'],
    queryFn: () => whatsappService.getQueueLogStats(),
  });

  const { data: routerData } = useQuery({
    queryKey: ['dashboardRouters', spopFilter],
    queryFn: () =>
      routerService.getRouters(1, 1, undefined, spopFilter ? { spop_id: spopFilter } : undefined),
  });

  const { data: spopStats } = useQuery({
    queryKey: ['dashboardSpopStats'],
    queryFn: async () => {
      const res = await spopService.getSPOPStats();
      return res.data?.data;
    },
    enabled: isAdmin,
  });

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount || 0);

  const paidAmount = billStats?.monthly?.paid?.total_amount ?? 0;
  const paidCount = billStats?.monthly?.paid?.count ?? 0;
  const pendingAmount = billStats?.monthly?.pending?.total_amount ?? 0;
  const pendingCount = billStats?.monthly?.pending?.count ?? 0;
  const overdueAmount = billStats?.monthly?.overdue?.total_amount ?? 0;
  const overdueCount = billStats?.monthly?.overdue?.count ?? 0;
  const todayDue = billStats?.today_due ?? 0;
  const todayDueAmount = billStats?.today_due_amount ?? 0;

  const prevPaid = prevBillStats?.monthly?.paid?.total_amount ?? 0;
  const revenueTrend =
    prevPaid > 0 ? Math.round(((paidAmount - prevPaid) / prevPaid) * 100) : undefined;

  const totalCustomers = customerTotal?.data?.pagination?.total ?? 0;
  const activeCustomers = customerActive?.data?.pagination?.total ?? 0;
  const suspendedCustomers = customerSuspended?.data?.pagination?.total ?? 0;

  const billChartData = [
    { name: 'Lunas', amount: paidAmount, count: paidCount },
    { name: 'Pending', amount: pendingAmount, count: pendingCount },
    { name: 'Tunggakan', amount: overdueAmount, count: overdueCount },
  ].filter((d) => d.count > 0 || d.amount > 0);

  const lineChartData = useMemo(() => {
    const raw = revenueChart?.data?.chartData || [];
    const sevenDaysAgo = subDays(now, 6);
    return raw
      .filter((item: { date: string }) => new Date(item.date) >= sevenDaysAgo)
      .map((item: { date: string; revenue: number }) => ({
        date: format(new Date(item.date), 'dd/MM', { locale: localeId }),
        revenue: item.revenue || 0,
      }));
  }, [revenueChart, now]);

  const monthlyTxCount =
    txStats?.data?.typeStats?.reduce((sum: number, s: { count?: number }) => sum + Number(s.count || 0), 0) ?? 0;
  const monthlyPaymentAmount =
    txStats?.data?.typeStats?.find((s: { type: string }) => s.type === 'payment')?.total_amount ?? 0;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboardBillStats'] });
    queryClient.invalidateQueries({ queryKey: ['dashboardCustomerTotal'] });
    queryClient.invalidateQueries({ queryKey: ['dashboardRecentBills'] });
    queryClient.invalidateQueries({ queryKey: ['dashboardTxStats'] });
    queryClient.invalidateQueries({ queryKey: ['dashboardRevenueChart'] });
    queryClient.invalidateQueries({ queryKey: ['dashboardWaQueue'] });
  };

  const getBillStatusChip = (status: string) => {
    const map: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
      paid: { label: 'Lunas', color: 'success' },
      pending: { label: 'Pending', color: 'warning' },
      overdue: { label: 'Tunggakan', color: 'error' },
      cancelled: { label: 'Batal', color: 'default' },
    };
    const cfg = map[status] || { label: status, color: 'default' as const };
    return <Chip label={cfg.label} color={cfg.color} size="small" />;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ringkasan billing & operasional — {monthLabel}
            {user?.role === 'operator' && user?.spop
              ? ` • SPOP ${user.spop.name}`
              : isAdmin
                ? ' • Semua SPOP'
                : ''}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} size="small">
          Refresh
        </Button>
      </Stack>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Total Pelanggan"
            value={String(totalCustomers)}
            subtitle={`${activeCustomers} aktif • ${suspendedCustomers} suspend`}
            icon={<People />}
            color={theme.palette.primary.main}
            loading={customersLoading}
            onClick={() => navigate('/customers')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Tagihan Lunas"
            value={formatCurrency(paidAmount)}
            subtitle={`${paidCount} tagihan • jatuh tempo ${monthLabel}`}
            icon={<CheckCircle />}
            color={theme.palette.success.main}
            loading={billLoading}
            trend={revenueTrend}
            onClick={() => navigate('/bills')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Menunggu Bayar"
            value={formatCurrency(pendingAmount)}
            subtitle={`${pendingCount} tagihan pending`}
            icon={<HourglassEmpty />}
            color={theme.palette.warning.main}
            loading={billLoading}
            onClick={() => navigate('/bills')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Tunggakan"
            value={formatCurrency(overdueAmount)}
            subtitle={
              todayDue > 0
                ? `${overdueCount} tunggakan • ${todayDue} jatuh tempo hari ini`
                : `${overdueCount} tagihan overdue`
            }
            icon={<Warning />}
            color={theme.palette.error.main}
            loading={billLoading}
            onClick={() => navigate('/bills')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Bill breakdown chart */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Status Tagihan — {monthLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Berdasarkan tanggal jatuh tempo dalam bulan berjalan
              </Typography>
              {billLoading ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </Box>
              ) : billChartData.length === 0 ? (
                <Alert severity="info">Belum ada data tagihan bulan ini</Alert>
              ) : (
                <Box height={260}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={billChartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis
                        type="number"
                        tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}jt`}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        formatter={(value: number, _name, props) => [
                          formatCurrency(value),
                          `${props.payload.count} tagihan`,
                        ]}
                      />
                      <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                        {billChartData.map((entry) => (
                          <Cell key={entry.name} fill={BILL_STATUS_COLORS[entry.name] || '#888'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment trend 7 days */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Pembayaran 7 Hari Terakhir
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Transaksi payment selesai (Tripay, PayBOS, manual)
              </Typography>
              {chartLoading ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </Box>
              ) : lineChartData.length === 0 ? (
                <Alert severity="info">Belum ada pembayaran dalam 7 hari terakhir</Alert>
              ) : (
                <Box height={260}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={lineChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}jt`} tick={{ fontSize: 11 }} />
                      <RechartsTooltip formatter={(v: number) => [formatCurrency(v), 'Pembayaran']} />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Quick links + summary */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Menu Cepat
              </Typography>
              <Stack spacing={1}>
                {QUICK_LINKS.map((link) => (
                  <Button
                    key={link.path}
                    variant="text"
                    fullWidth
                    startIcon={link.icon}
                    endIcon={<ArrowForward fontSize="small" />}
                    onClick={() => navigate(link.path)}
                    sx={{
                      justifyContent: 'flex-start',
                      color: 'text.primary',
                      '&:hover': { bgcolor: alpha(link.color, 0.08) },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Ringkasan Operasional
              </Typography>
              <Stack spacing={1.5}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Jatuh tempo hari ini
                    </Typography>
                    <Chip
                      icon={<Today fontSize="small" />}
                      label={formatCurrency(todayDueAmount)}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {todayDue} tagihan
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Transaksi bulan ini
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {monthlyTxCount}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Pembayaran: {formatCurrency(Number(monthlyPaymentAmount))}
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Antrian WhatsApp
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="warning.main">
                      {(waQueueStats?.totalPending ?? 0).toLocaleString()} pending
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={
                      waQueueStats?.totalSent
                        ? Math.min(
                            100,
                            ((waQueueStats.totalSent || 0) /
                              ((waQueueStats.totalSent || 0) + (waQueueStats.totalPending || 0) + 1)) *
                              100
                          )
                        : 0
                    }
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {waQueueStats?.totalSent ?? 0} terkirim • {waQueueStats?.totalFailed ?? 0} gagal
                  </Typography>
                </Box>

                {isAdmin && spopStats && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      SPOP aktif
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {spopStats.active} / {spopStats.total}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Router terdaftar
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {routerData?.data?.pagination?.total ?? 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent bills */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Tagihan Terbaru
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Jatuh tempo bulan {monthLabel}
                  </Typography>
                </Box>
                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/bills')}>
                  Lihat semua
                </Button>
              </Stack>

              {billsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell>No. Tagihan</TableCell>
                        <TableCell>Pelanggan</TableCell>
                        <TableCell align="right">Jumlah</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Jatuh Tempo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(recentBills?.data?.bills || []).length > 0 ? (
                        recentBills.data.bills.map((bill: {
                          id: string;
                          bill_number: string;
                          customer?: { name?: string; customer_code?: string };
                          total_amount: number;
                          status: string;
                          due_date: string;
                        }) => (
                          <TableRow
                            key={bill.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate('/bills')}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {bill.bill_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                                {bill.customer?.name || '-'}
                              </Typography>
                              {bill.customer?.customer_code && (
                                <Typography variant="caption" color="text.secondary">
                                  {bill.customer.customer_code}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(bill.total_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>{getBillStatusChip(bill.status)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {format(new Date(bill.due_date), 'dd/MM/yyyy')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">Tidak ada tagihan bulan ini</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
                Pelanggan Terdaftar Terbaru
              </Typography>
              {recentCustomersLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Stack spacing={1}>
                  {(recentCustomers?.data?.customers || []).map((c: Customer & { package?: { name?: string } }) => (
                    <Stack
                      key={c.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate('/customers')}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {c.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.package?.name || 'Tanpa paket'} •{' '}
                          {format(new Date(c.created_at), 'dd/MM/yyyy')}
                        </Typography>
                      </Box>
                      <Chip
                        label={c.status === 'active' ? 'Aktif' : c.status}
                        size="small"
                        color={c.status === 'active' ? 'success' : 'default'}
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
